/**
 * Native "leave us a review" prompt — SKStoreReviewController on iOS, Play
 * In-App Review on Android, via @capacitor-community/in-app-review.
 *
 * HOW OTHER APPS DO IT (and Apple's rules): the OS shows its own star sheet,
 * caps it at ~3 displays/year, and silently ignores extra requests — you
 * cannot know if it showed. So the job here is picking GOOD moments and
 * self-throttling so the OS quota is never wasted on a bad one:
 *   • never before the 5th app launch or the 3rd day — new users haven't
 *     formed an opinion yet
 *   • never within 60 days of the last ask
 *   • only after a POSITIVE beat (finished a real call, boot of a warmed-up
 *     session), with a dice roll so it feels organic, not scheduled
 * All state is local to the device (localStorage) — this never blocks or
 * gates anything, per store rules.
 */
import { Capacitor } from '@capacitor/core';

const K_LAUNCHES = 'ic_review_launches';
const K_FIRST = 'ic_review_first_launch';
const K_LAST = 'ic_review_last_prompt';

const MIN_LAUNCHES = 5;
const MIN_DAYS_INSTALLED = 3;
const COOLDOWN_DAYS = 60;

const num = (k: string) => {
  try { return Number(localStorage.getItem(k)) || 0; } catch { return 0; }
};
const set = (k: string, v: number) => {
  try { localStorage.setItem(k, String(v)); } catch { /* noop */ }
};

/** Call once per app boot (m/layout does). */
export function recordAppLaunch(): void {
  if (!Capacitor.isNativePlatform()) return;
  set(K_LAUNCHES, num(K_LAUNCHES) + 1);
  if (!num(K_FIRST)) set(K_FIRST, Date.now());
}

/**
 * Ask for a review if this is a good moment. `chance` is the dice roll
 * (0..1) so different triggers can weight themselves.
 */
export async function maybeRequestReview(trigger: string, chance = 0.3): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const days = (ms: number) => (Date.now() - ms) / 86_400_000;
    if (num(K_LAUNCHES) < MIN_LAUNCHES) return;
    const first = num(K_FIRST);
    if (!first || days(first) < MIN_DAYS_INSTALLED) return;
    const last = num(K_LAST);
    if (last && days(last) < COOLDOWN_DAYS) return;
    if (Math.random() > chance) return;

    const { InAppReview } = await import('@capacitor-community/in-app-review');
    await InAppReview.requestReview();
    set(K_LAST, Date.now());
    console.log(`[review] requested (trigger: ${trigger})`);
  } catch (e) {
    console.warn('[review] request failed:', e);
  }
}
